/**
 * CSV export helpers for admin reporting downloads.
 */

/**
 * Escape a cell value for CSV (RFC 4180-style quoting).
 * @param {unknown} value
 * @returns {string}
 */
export const escapeCsvField = (value) => {
  if (value == null) return '';

  const stringValue = String(value);
  if (/[",\n\r]/.test(stringValue)) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }

  return stringValue;
};

/**
 * Convert header + row arrays into a CSV string.
 * @param {string[]} headers
 * @param {Array<Array<unknown>>} rows
 * @returns {string}
 */
export const buildCsvContent = (headers, rows) => {
  const headerLine = headers.map(escapeCsvField).join(',');
  const dataLines = rows.map((row) => row.map(escapeCsvField).join(','));
  return [headerLine, ...dataLines].join('\r\n');
};

/**
 * Trigger a CSV file download in the browser.
 * @param {string} filename
 * @param {string} csvContent
 */
export const downloadCsvFile = (filename, csvContent) => {
  const blob = new Blob([`\uFEFF${csvContent}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Build a filesystem-safe slug from an organization name.
 * @param {string} name
 * @returns {string}
 */
export const slugifyForFilename = (name) => {
  if (!name) return 'organization';

  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '') || 'organization';
};

/**
 * Format a timestamp for CSV export (ISO when possible, else display fallback).
 * @param {unknown} value
 * @param {(value: unknown) => string} formatDisplayDate
 * @returns {string}
 */
const formatCsvDate = (value, formatDisplayDate) => {
  if (!value) return '';

  try {
    const date = value.toDate ? value.toDate() : new Date(value);
    if (!Number.isNaN(date.getTime())) {
      return date.toISOString();
    }
  } catch (err) {
    // Fall through to display formatter
  }

  const formatted = formatDisplayDate(value);
  return formatted === 'Never' ? '' : formatted;
};

/**
 * Extract numeric resource metrics for CSV columns.
 * @param {Object|undefined} metrics
 * @returns {{ accessedUnique: number|string, accessedTotal: number|string, completedUnique: number|string }}
 */
const getResourceMetrics = (metrics) => ({
  accessedUnique: metrics?.accessedUnique ?? '',
  accessedTotal: metrics?.accessedTotal ?? '',
  completedUnique: metrics?.completedUnique ?? ''
});

/**
 * Build CSV headers and rows for the Admin Students table.
 * @param {Array<Object>} students
 * @param {Object<string, Object>} userStatsById
 * @param {string} organizationName
 * @param {(value: unknown) => string} formatDisplayDate
 * @returns {{ headers: string[], rows: Array<Array<unknown>> }}
 */
export const buildStudentsExportRows = (
  students,
  userStatsById,
  organizationName,
  formatDisplayDate
) => {
  const headers = [
    'Organization',
    'Email',
    'Display Name',
    'Last Login',
    'Logins',
    'Courses Accessed (Unique)',
    'Courses Accessed (Visits)',
    'Courses Completed',
    'Lessons Accessed (Unique)',
    'Lessons Accessed (Visits)',
    'Lessons Completed',
    'Prompts Accessed (Unique)',
    'Prompts Accessed (Visits)',
    'Prompts Completed'
  ];

  const rows = students.map((student) => {
    const stats = userStatsById[student.id];
    const courses = getResourceMetrics(stats?.courses);
    const lessons = getResourceMetrics(stats?.lessons);
    const prompts = getResourceMetrics(stats?.prompts);

    return [
      organizationName,
      student.email,
      student.display_name || '',
      formatCsvDate(stats?.lastLogin, formatDisplayDate),
      stats?.logins ?? '',
      courses.accessedUnique,
      courses.accessedTotal,
      courses.completedUnique,
      lessons.accessedUnique,
      lessons.accessedTotal,
      lessons.completedUnique,
      prompts.accessedUnique,
      prompts.accessedTotal,
      prompts.completedUnique
    ];
  });

  return { headers, rows };
};

/**
 * Export the Admin Students table to a CSV file download.
 * @param {Object} options
 * @param {Array<Object>} options.students
 * @param {Object<string, Object>} options.userStatsById
 * @param {string} options.organizationName
 * @param {(value: unknown) => string} options.formatDisplayDate
 */
export const exportStudentsToCsv = ({
  students,
  userStatsById,
  organizationName,
  formatDisplayDate
}) => {
  const { headers, rows } = buildStudentsExportRows(
    students,
    userStatsById,
    organizationName,
    formatDisplayDate
  );

  const csvContent = buildCsvContent(headers, rows);
  const dateStamp = new Date().toISOString().slice(0, 10);
  const orgSlug = slugifyForFilename(organizationName);
  const filename = `students-${orgSlug}-${dateStamp}.csv`;

  downloadCsvFile(filename, csvContent);
};
